import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Ayuda del módulo de bodega: explica el modelo de posiciones y da recetas
// concretas para armarla.
//
// Va como diálogo y no como página de documentación aparte a propósito: las
// dudas aparecen con el formulario abierto, y una ayuda que obliga a salir de la
// pantalla no se lee. El contenido describe el comportamiento real del
// generador, incluidos los casos en que regenerar NO actualiza lo que ya existe.

const Code = ({ children }) => (
    <Box
        component="span"
        sx={{
            fontFamily: "monospace",
            fontSize: "0.85em",
            px: 0.5,
            py: 0.15,
            borderRadius: 0.5,
            bgcolor: "action.hover",
        }}
    >
        {children}
    </Box>
);

const CODE_DIAGRAM = `A-01-03-B-02
│  │  │ │  └── posición: hueco dentro del nivel
│  │  │ └───── nivel: letra, A = piso, hacia arriba
│  │  └─────── rack: bahía dentro del pasillo
│  └────────── pasillo
└───────────── zona (se puede omitir)`;

const EXAMPLES = [
    {
        caso: "Bodega chica, estantes sin niveles",
        receta: "Zona vacía · Pasillo 1 · Racks 6 · Niveles 1 · Posiciones 1",
        resultado: "01-01 … 01-06",
        total: "6 posiciones",
    },
    {
        caso: "Rack selectivo típico",
        receta: "Zona A · Pasillo 1 · Racks 10 · Niveles 4 · Posiciones 1",
        resultado: "A-01-01-A … A-01-10-D",
        total: "40 posiciones",
    },
    {
        caso: "Rack con 3 huecos por nivel",
        receta: "Zona A · Pasillo 2 · Racks 12 · Niveles 4 · Posiciones 3",
        resultado: "A-02-01-A-01 … A-02-12-D-03",
        total: "144 posiciones",
    },
    {
        caso: "Andén de recepción",
        receta: "Zona R · Pasillo 1 · Racks 4 · Niveles 1 · Posiciones 1 · Tipo Recepción",
        resultado: "R-01-01 … R-01-04",
        total: "4 posiciones",
    },
];

const FIELDS = [
    ["Zona", "Primer segmento del código, 1 o 2 letras. Opcional: sin zona el código arranca en el pasillo."],
    ["Pasillo", "Lo único obligatorio."],
    ["Racks", "Bahías a lo largo del pasillo. Máximo 200."],
    ["Niveles", "Alturas del rack. A es el piso y se sube: B, C, D… Máximo 26."],
    ["Posiciones por nivel", "Huecos dentro de cada nivel. Máximo 50."],
    ["Tipo", "Para qué se usa la posición: picking, reserva, recepción, despacho, staging o devoluciones."],
    ["Orientación", "Cómo se dibuja el pasillo en el tablero: horizontal corre los racks hacia la derecha, vertical hacia abajo."],
    ["Col. / Fila", "Dónde arranca el pasillo en el tablero. Es solo dibujo: no cambia ningún código."],
];

const WarehouseHelpDialog = ({ open, onClose, defaultSection = "posicion" }) => {
    const [expanded, setExpanded] = useState(defaultSection);

    useEffect(() => {
        if (open) setExpanded(defaultSection);
    }, [open, defaultSection]);

    const section = (id) => ({
        expanded: expanded === id,
        onChange: (_event, isExpanded) => setExpanded(isExpanded ? id : false),
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>
                Cómo armar la bodega
                <Typography variant="body2" color="text.secondary">
                    El modelo de posiciones, los campos del generador y ejemplos listos para copiar
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Accordion {...section("posicion")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Qué es una posición y cómo se lee el código</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Una posición es el hueco exacto donde vive un producto: el lugar al que camina el
                            preparador. El código sigue el estándar de bodega{" "}
                            <Code>ZONA-PASILLO-RACK-NIVEL-POSICION</Code>.
                        </Typography>

                        <Box
                            component="pre"
                            sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                lineHeight: 1.6,
                                p: 2,
                                m: 0,
                                mb: 2,
                                overflowX: "auto",
                                borderRadius: 1,
                                bgcolor: "action.hover",
                            }}
                        >
                            {CODE_DIAGRAM}
                        </Box>

                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Los números van con ceros a la izquierda, y eso no es estética. Con segmentos de ancho
                            fijo, el orden alfabético del código es el orden físico del recorrido: la lista de
                            preparación sale ordenada sola. Sin relleno, <Code>A-10</Code> se ordenaría antes que{" "}
                            <Code>A-2</Code> y el recorrido saldría mezclado.
                        </Typography>

                        <Typography variant="body2">
                            El código se acorta cuando no hace falta: con un solo nivel y una sola posición queda en{" "}
                            <Code>A-01-03</Code>, porque arrastrar un <Code>-A-01</Code> constante alarga la etiqueta
                            sin decir nada nuevo.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("pasos")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Armar la bodega de cero, en 5 pasos</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" component="div">
                            <ol style={{ paddingLeft: 20, margin: 0 }}>
                                <li style={{ marginBottom: 10 }}>
                                    <strong>Generá los pasillos.</strong> Un pasillo real, una corrida del generador.
                                    Si ya tenés las posiciones en una planilla, usá <strong>Importar</strong> con la
                                    plantilla y te ahorrás este paso.
                                </li>
                                <li style={{ marginBottom: 10 }}>
                                    <strong>Acomodá los racks en el tablero.</strong> Arrastralos hasta que el plano se
                                    parezca a la bodega de verdad. Col. y Fila del generador son solo el punto de
                                    partida.
                                </li>
                                <li style={{ marginBottom: 10 }}>
                                    <strong>Recalculá el recorrido</strong>, que es lo que ordena las listas de
                                    picking. Ojo: por defecto el orden sale del código de posición, no del plano —
                                    mirá la sección del recorrido para ver la diferencia.
                                </li>
                                <li style={{ marginBottom: 10 }}>
                                    <strong>Asigná los SKUs</strong> abriendo cada rack, o dejá que lleguen desde el
                                    ERP si tu ERP maneja ubicaciones.
                                </li>
                                <li>
                                    <strong>Imprimí las etiquetas</strong> y pegalas en la viga del rack.
                                </li>
                            </ol>
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("campos")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Generar un pasillo, campo por campo</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table size="small">
                            <TableBody>
                                {FIELDS.map(([name, help]) => (
                                    <TableRow key={name}>
                                        <TableCell sx={{ width: 170, verticalAlign: "top", fontWeight: 500 }}>
                                            {name}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{help}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            La cuenta es simple: <strong>racks × niveles × posiciones</strong>. El cartel del
                            formulario te muestra el total y cómo va a quedar la primera posición antes de crear
                            nada.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("ejemplos")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Ejemplos listos para copiar</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Caso</TableCell>
                                        <TableCell>Qué poner</TableCell>
                                        <TableCell>Códigos que salen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {EXAMPLES.map((row) => (
                                        <TableRow key={row.caso}>
                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                <Typography variant="body2">{row.caso}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.receta}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                                    {row.resultado}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.total}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("regenerar")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Regenerar un pasillo: qué actualiza y qué no</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            El generador reconoce las posiciones <strong>por su código</strong>: si el código ya
                            existe lo actualiza, y si no, lo crea. De ahí salen tres comportamientos que conviene
                            tener claros.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Ampliar funciona sin riesgo.</strong> Correr el mismo pasillo con 12 racks en vez
                            de 10 agrega los dos que faltan y no toca los demás.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Reducir no borra.</strong> Correrlo con 8 racks deja los racks 09 y 10 donde
                            estaban. Para sacarlos hay que borrarlos, el generador nunca elimina.
                        </Typography>

                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Cambiar la zona, el pasillo o la cantidad de niveles crea un set nuevo.</strong>{" "}
                                Si regenerás con zona <Code>B</Code> en vez de <Code>A</Code>, o con 1 nivel en vez de
                                4, los códigos cambian y las posiciones viejas quedan igual: terminás con las dos
                                versiones. Si te equivocaste en esos campos, corregí borrando las posiciones anteriores
                                en vez de volver a generar.
                            </Typography>
                        </Alert>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("recorrido")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">El recorrido de picking</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Es el punto de tener posiciones. El recorrido se calcula en serpentina: el pasillo 1 se
                            camina del rack 01 al 10, el pasillo 2 del 10 al 01, y así. El preparador no vuelve en
                            vacío. Dentro de un rack siempre se va de abajo hacia arriba, sin importar el sentido del
                            pasillo.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Se recalcula solo cada vez que generás un pasillo o importás una planilla, y a mano con{" "}
                            <strong>Recalcular recorrido</strong>.
                        </Typography>

                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            De dónde sale el orden
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Cada bodega elige entre dos formas, y la diferencia importa:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Por código de posición</strong> — el orden sale del código: zona, pasillo y rack.
                            Es como funcionó siempre y es lo que trae cada bodega por defecto. Acá{" "}
                            <strong>mover un rack en el tablero no cambia el recorrido</strong>: el plano es dibujo, y
                            el orden lo manda el código.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Por el plano de la bodega</strong> — el orden sale de la geometría: primero las
                            zonas en el orden que les pusiste, después la serpentina sobre las coordenadas. Acá sí:
                            movés un rack, recalculás, y las listas salen en el orden nuevo.
                        </Typography>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Hoy el modo se cambia por API, todavía no hay pantalla para hacerlo. Si querés pasar
                                una bodega al modo por plano, pedilo — conviene hacerlo cuando su plano ya refleje la
                                bodega real, y comparando una lista de preparación antes y después.
                            </Typography>
                        </Alert>

                        <Typography variant="body2">
                            Las posiciones se numeran de 10 en 10, así queda hueco para intercalar una a mano después
                            sin renumerar la bodega entera.
                        </Typography>
                    </AccordionDetails>
                </Accordion>

                <Accordion {...section("etiquetas")} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Etiquetas de posición</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            El botón <strong>Etiquetas</strong> muestra cómo va a quedar la etiqueta, deja elegir la
                            impresora y la manda a la Zebra. Van pegadas en la viga del rack: el código en fuente
                            grande para leerlo caminando, y el código de barras para que el preparador confirme la
                            posición con el mismo lector con que escanea productos.
                        </Typography>
                        <Alert severity="warning">
                            <Typography variant="body2">
                                La geometría de estas etiquetas todavía no está calibrada en hardware. Imprimí una
                                tira de 3 o 4, medila y ajustá antes de etiquetar la bodega completa.
                            </Typography>
                        </Alert>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default WarehouseHelpDialog;
